import { ILandingGeometry, IStairCalculationResult, IStairConfig, IStepGeometry } from '@stair-craft/common/util';

import { horizontalDim, verticalDim } from './dim-builders';
import { pushElevationRunLengths, pushElevationTreadMarks } from './elevation-marks';
import { IElevationMap, pushElevationRailings } from './elevation-railing';
import {
    EColorRef,
    ELineCap,
    EPrimitiveKind,
    ETextAlign,
    ETextBaseline,
    ETextRole,
    FONT_MONO_FAMILY,
    IFont,
    IPaneRect,
    IPlanPrimitive,
    IPoint,
    IPolyPrimitive,
    IProjectionOutput,
    ITextPrimitive,
    IViewFit,
    emptyProjection,
} from './plan-primitive';
import { projectSideViewLU } from './side-view-lu';
import { ITextMeasurer } from './text-measurer';

export const PANE_LABEL_INSET: number = 14;
export const STRINGER_THICKNESS: number = 280;
export const RISER_THICKNESS: number = 22;
export const SIDE_VIEW_REF_HEIGHT_MM: number = 4200;

/** How far under the floor line a spiral's pole-width dimension runs, pixels. */
const SPIRAL_POLE_DIM_OFFSET: number = 26;
const F_500_10: IFont = { weight: 500, size: 10, family: FONT_MONO_FAMILY };
const F_500_9: IFont = { weight: 500, size: 9, family: FONT_MONO_FAMILY };
export const F_400_9: IFont = { weight: 400, size: 9, family: FONT_MONO_FAMILY };
const F_400_8: IFont = { weight: 400, size: 8, family: FONT_MONO_FAMILY };
export const F_400_10: IFont = { weight: 400, size: 10, family: FONT_MONO_FAMILY };
export const F_400_11: IFont = { weight: 400, size: 11, family: FONT_MONO_FAMILY };

export type IMapFn = (n: number) => number;

/** An elevation's scale and the pane point of its millimetre origin. */
export interface IElevationFrame {
    scale: number;
    ox: number;
    oy: number;
}

/**
 * The frame an elevation draws in: the sheet's fit when it gives one, otherwise the view's own
 * fit into its pane (null when the pane is too small to draw in).
 */
export function elevationFrame<T extends IElevationFrame | null>(fit: IViewFit | undefined, own: () => T): IElevationFrame | T {
    return fit ? { scale: fit.pxPerMm, ox: fit.origin.x, oy: fit.origin.y } : own();
}

function rectPoints(x: number, y: number, w: number, h: number): IPoint[] {
    return [
        [x, y],
        [x + w, y],
        [x + w, y + h],
        [x, y + h],
    ];
}

export function fillRect(x: number, y: number, w: number, h: number, fill: EColorRef, fillAlpha?: number): IPolyPrimitive {
    return { kind: EPrimitiveKind.Poly, points: rectPoints(x, y, w, h), closed: true, fill, fillAlpha };
}

export function fillStrokeRect(
    x: number,
    y: number,
    w: number,
    h: number,
    fill: EColorRef,
    stroke: EColorRef,
    width: number
): IPolyPrimitive {
    return { kind: EPrimitiveKind.Poly, points: rectPoints(x, y, w, h), closed: true, fill, stroke, width };
}

export function strokePoly(points: IPoint[], fill: EColorRef, stroke: EColorRef, width: number): IPolyPrimitive {
    return { kind: EPrimitiveKind.Poly, closed: true, points, fill, stroke, width };
}

export function line(
    from: IPoint,
    to: IPoint,
    stroke: EColorRef,
    width: number,
    options?: { dash?: readonly number[]; cap?: ELineCap }
): IPolyPrimitive {
    return {
        kind: EPrimitiveKind.Poly,
        points: [from, to],
        closed: false,
        dash: options?.dash,
        cap: options?.cap,
        stroke,
        width,
    };
}

export function text(at: IPoint, value: string, align: ETextAlign, baseline: ETextBaseline, color: EColorRef, font: IFont): ITextPrimitive {
    return { kind: EPrimitiveKind.Text, text: value, at, align, baseline, color, font };
}

/** The text as a note about the whole view: the sheet sets it above the drawing. */
export function note(label: ITextPrimitive): ITextPrimitive {
    return { ...label, role: ETextRole.Note };
}

/** The text as a floor-level label: the sheet sets it beside the drawing. */
export function level(label: ITextPrimitive): ITextPrimitive {
    return { ...label, role: ETextRole.Level };
}

interface ISegment {
    kind: 'step' | 'landing';
    xStart: number;
    xEnd: number;
    yTop: number;
    yBottom: number;
    isWinder?: boolean;
}

interface ILanding {
    y: number;
    depth: number;
    thickness: number;
}

interface IStraightCtx {
    result: IStairCalculationResult;
    config: IStairConfig;
    segments: ISegment[];
    wx: IMapFn;
    wy: IMapFn;
    ws: IMapFn;
    viewTopY: number;
    totalRise: number;
    totalUnfoldRun: number;
    worldXMin: number;
    slab: number;
    railingH: number;
    treadT: number;
    nose: number;
    h: number;
    bAvg: number;
    cosA: number;
    sinA: number;
    angleRad: number;
    isMonoStringer: boolean;
    truncatedAtTurn: boolean;
}

/** Extracted from `projectSideViewStraight` to keep its complexity low: unfolds sorted steps + landings into ordered profile segments. */
function buildStraightSegments(sortedSteps: IStepGeometry[], sortedLandings: ILanding[]): ISegment[] {
    const segments: ISegment[] = [];
    let cumX: number = 0;
    let landingIdx: number = 0;
    let prevY: number = 0;

    for (const step of sortedSteps) {
        while (landingIdx < sortedLandings.length) {
            const ld: ILanding = sortedLandings[landingIdx];
            if (ld.y >= prevY - 1 && ld.y < step.position.y - 1) {
                segments.push({ kind: 'landing', xStart: cumX, xEnd: cumX + ld.depth, yBottom: ld.y - ld.thickness, yTop: ld.y });
                cumX += ld.depth;
                landingIdx++;
            } else {
                break;
            }
        }
        const stepDepth: number = step.isWinder ? (step.outerDepth ?? step.treadDepth) : step.treadDepth;
        // `position.y` is the walking surface — the top of the tread, as the 3D model takes it.
        const yTop: number = step.position.y;
        segments.push({
            kind: 'step',
            xStart: cumX,
            xEnd: cumX + stepDepth,
            yBottom: yTop - step.treadThickness,
            isWinder: step.isWinder,
            yTop,
        });
        cumX += stepDepth;
        prevY = yTop;
    }
    while (landingIdx < sortedLandings.length) {
        const ld: ILanding = sortedLandings[landingIdx];
        segments.push({ kind: 'landing', xStart: cumX, xEnd: cumX + ld.depth, yBottom: ld.y - ld.thickness, yTop: ld.y });
        cumX += ld.depth;
        landingIdx++;
    }
    return segments;
}

/** Extracted from `projectSideViewStraight` to keep its complexity low: draws the upper floor slab + opening cut (straight) or the turn marker (L/U). */
function pushStraightFloor2(primitives: IPlanPrimitive[], ctx: IStraightCtx): void {
    const { wx, wy, ws, viewTopY, totalRise, slab, totalUnfoldRun, worldXMin, result, truncatedAtTurn } = ctx;
    const yTopSurf: number = wy(viewTopY);

    if (!truncatedAtTurn) {
        const yTopUnder: number = wy(totalRise + slab);
        const openingLen: number = Math.min(Math.max(800, result.room.openingLength * 0.6), totalUnfoldRun * 0.65);
        const slabEnd: number = totalUnfoldRun - openingLen;

        primitives.push(fillRect(wx(worldXMin), yTopUnder, wx(slabEnd) - wx(worldXMin), ws(slab), EColorRef.Ink, 0.08));

        primitives.push(line([wx(worldXMin), yTopSurf], [wx(slabEnd), yTopSurf], EColorRef.Ink, 1));
        primitives.push(line([wx(worldXMin), yTopUnder], [wx(slabEnd), yTopUnder], EColorRef.Ink, 1));

        primitives.push(line([wx(slabEnd), yTopSurf], [wx(slabEnd), yTopUnder], EColorRef.Opening, 1.5, { dash: [5, 4] }));
        primitives.push(line([wx(slabEnd), yTopSurf], [wx(totalUnfoldRun), yTopSurf], EColorRef.Opening, 1.5, { dash: [5, 4] }));

        primitives.push(
            text(
                [wx(slabEnd + openingLen / 2), yTopSurf - 8],
                $localize`:@@planOpeningLabel:ПРОЁМ`,
                ETextAlign.Center,
                ETextBaseline.Middle,
                EColorRef.Opening,
                F_500_10
            )
        );

        primitives.push(
            level(
                text(
                    [wx(worldXMin) + 6, yTopSurf - 3],
                    $localize`:@@planLevelUpper:2-Й ЭТАЖ`,
                    ETextAlign.Left,
                    ETextBaseline.Bottom,
                    EColorRef.Ink3,
                    F_400_9
                )
            )
        );
    } else {
        // L/U: indicate that the stair turns and the second flight is in plane
        primitives.push(
            text(
                [wx(totalUnfoldRun) + 12, yTopSurf - 14],
                $localize`:@@planLabelTurnArrow:ПОВОРОТ →`,
                ETextAlign.Right,
                ETextBaseline.Middle,
                EColorRef.Ink3,
                F_500_10
            )
        );
        primitives.push(
            text(
                [wx(totalUnfoldRun) + 12, yTopSurf],
                '(2-й марш см. план)',
                ETextAlign.Right,
                ETextBaseline.Middle,
                EColorRef.Ink3,
                F_400_8
            )
        );
    }
}

/** Extracted from `projectSideViewStraight` to keep its complexity low: косоур sawtooth stringer profile (central beam below the steps). */
function buildStraightKosourPoints(ctx: IStraightCtx): IPoint[] {
    const { wx, wy, segments, viewTopY, totalUnfoldRun, cosA, sinA } = ctx;
    const stringerPoints: IPoint[] = [];
    const yBottomAtRight: number = viewTopY - STRINGER_THICKNESS / Math.max(0.001, cosA);
    const xFloorCross: number = sinA > 0.001 ? STRINGER_THICKNESS / sinA : 0;
    stringerPoints.push([wx(0), wy(0)]);
    let prevSegYTop: number = 0;
    for (const seg of segments) {
        if (Math.abs(seg.yTop - prevSegYTop) > 0.5) {
            stringerPoints.push([wx(seg.xStart), wy(seg.yTop)]);
        }
        stringerPoints.push([wx(seg.xEnd), wy(seg.yTop)]);
        prevSegYTop = seg.yTop;
    }
    // Vertical cut at right edge
    if (yBottomAtRight > 0) {
        stringerPoints.push([wx(totalUnfoldRun), wy(yBottomAtRight)]);
        if (xFloorCross > 0 && xFloorCross < totalUnfoldRun) {
            stringerPoints.push([wx(xFloorCross), wy(0)]);
        }
    }
    stringerPoints.push([wx(0), wy(0)]);
    return stringerPoints;
}

/** Extracted from `projectSideViewStraight` to keep its complexity low: тетива side-plank stringer profile (steps housed inside the plank). */
function buildStraightTetivaPoints(ctx: IStraightCtx): IPoint[] {
    const { wx, wy, viewTopY, totalUnfoldRun, cosA, h, bAvg } = ctx;
    const stringerPoints: IPoint[] = [];
    const slope: number = h / Math.max(1, bAvg); // = (h/b)
    const offsetTop: number = h; // plank top at x=0 sits at first step's tread level
    const planeThickness: number = STRINGER_THICKNESS / Math.max(0.001, cosA); // vertical thickness of plank
    const xTopUpperFloor: number = (viewTopY - offsetTop) / Math.max(0.0001, slope); // diag meets upper floor
    const offsetBottom: number = offsetTop - planeThickness; // y-intercept of plank's bottom edge at x=0
    const xBottomFloorCross: number = offsetBottom < 0 ? -offsetBottom / Math.max(0.0001, slope) : 0;
    const yBottomAtRight: number = viewTopY - planeThickness;

    stringerPoints.push([wx(0), wy(0)]);
    stringerPoints.push([wx(0), wy(offsetTop)]);
    stringerPoints.push([wx(Math.min(xTopUpperFloor, totalUnfoldRun)), wy(viewTopY)]);
    if (xTopUpperFloor < totalUnfoldRun) {
        stringerPoints.push([wx(totalUnfoldRun), wy(viewTopY)]);
    }
    if (yBottomAtRight > 0) {
        stringerPoints.push([wx(totalUnfoldRun), wy(yBottomAtRight)]);
        if (xBottomFloorCross > 0 && xBottomFloorCross < totalUnfoldRun) {
            stringerPoints.push([wx(xBottomFloorCross), wy(0)]);
        }
    } else {
        stringerPoints.push([wx(totalUnfoldRun), wy(0)]);
    }
    stringerPoints.push([wx(0), wy(0)]);
    return stringerPoints;
}

/** Extracted from `projectSideViewStraight` to keep its complexity low: pushes the stringer polygon (косоур or тетива). */
function pushStraightStringer(primitives: IPlanPrimitive[], ctx: IStraightCtx): void {
    const stringerPoints: IPoint[] = ctx.isMonoStringer ? buildStraightKosourPoints(ctx) : buildStraightTetivaPoints(ctx);
    primitives.push(strokePoly(stringerPoints, EColorRef.Paper2, EColorRef.Ink2, 1));
}

/** Extracted from `projectSideViewStraight` to keep its complexity low: risers + tread boards sitting on the косоур zigzag. */
function pushStraightKosourSteps(primitives: IPlanPrimitive[], ctx: IStraightCtx): void {
    const { wx, wy, segments, treadT, nose } = ctx;
    // Risers (vertical face boards) — at xStart of each step segment; an open stair has none
    let lastTopY: number = 0;
    for (const seg of ctx.result.geometry.risers ? segments : []) {
        if (seg.kind === 'step' && Math.abs(seg.yTop - lastTopY) > 0.5) {
            const xL: number = wx(seg.xStart - RISER_THICKNESS);
            const xR: number = wx(seg.xStart);
            const yT: number = wy(seg.yTop - treadT);
            const yB: number = wy(lastTopY);
            primitives.push(fillStrokeRect(xL, yT, xR - xL, yB - yT, EColorRef.Landing, EColorRef.StepStroke, 1));
        }
        lastTopY = seg.yTop;
    }
    // Tread boards (with nose) on TOP of the zigzag
    for (const seg of segments) {
        const fill: EColorRef = seg.kind === 'landing' ? EColorRef.Landing : EColorRef.Step;
        const xL: number = wx(seg.kind === 'step' ? seg.xStart - nose : seg.xStart);
        const xR: number = wx(seg.xEnd);
        const yT: number = wy(seg.yTop);
        const yB: number = wy(seg.yTop - treadT);
        primitives.push(fillStrokeRect(xL, yT, xR - xL, yB - yT, fill, EColorRef.StepStroke, 1));

        if (seg.kind === 'landing') {
            primitives.push(
                text(
                    [(wx(seg.xStart) + wx(seg.xEnd)) / 2, yT - 4],
                    'ПЛОЩАДКА',
                    ETextAlign.Center,
                    ETextBaseline.Bottom,
                    EColorRef.Ink3,
                    F_500_9
                )
            );
        }
    }
}

/** Extracted from `projectSideViewStraight` to keep its complexity low: treads housed as dashed lines inside the тетива plank. */
function pushStraightTetivaSteps(primitives: IPlanPrimitive[], ctx: IStraightCtx): void {
    const { wx, wy, segments, result } = ctx;
    for (const seg of segments) {
        primitives.push(line([wx(seg.xStart), wy(seg.yTop)], [wx(seg.xEnd), wy(seg.yTop)], EColorRef.StepStroke, 1, { dash: [4, 3] }));
        if (seg.kind === 'step') {
            const prevTopY: number = seg.xStart === 0 ? 0 : seg.yTop - result.riserHeight;
            primitives.push(
                line([wx(seg.xStart), wy(seg.yTop)], [wx(seg.xStart), wy(prevTopY)], EColorRef.StepStroke, 1, { dash: [4, 3] })
            );
        }
    }

    // Landing labels still visible
    for (const seg of segments) {
        if (seg.kind === 'landing') {
            primitives.push(
                text(
                    [(wx(seg.xStart) + wx(seg.xEnd)) / 2, wy(seg.yTop) - 4],
                    'ПЛОЩАДКА',
                    ETextAlign.Center,
                    ETextBaseline.Bottom,
                    EColorRef.Ink3,
                    F_500_9
                )
            );
        }
    }
}

/** Extracted from `projectSideViewStraight` to keep its complexity low: risers/treads dispatch by stringer type. */
function pushStraightSteps(primitives: IPlanPrimitive[], ctx: IStraightCtx): void {
    if (ctx.isMonoStringer) {
        pushStraightKosourSteps(primitives, ctx);
    } else {
        pushStraightTetivaSteps(primitives, ctx);
    }
}

/** The unfolded profile of a straight stair runs along the stair's own Z. */
function straightElevation(ctx: IStraightCtx): IElevationMap {
    return { across: (point: { x: number; z: number }): number => point.z, wx: ctx.wx, wy: ctx.wy };
}

/**
 * Extracted from `projectSideViewStraight` to keep its complexity low: the railing from the
 * calculation's railing geometry — the unfolded profile runs along the stair's own Z.
 */
function pushStraightRailing(primitives: IPlanPrimitive[], ctx: IStraightCtx): void {
    pushElevationRailings(primitives, ctx.result, ctx.config, straightElevation(ctx));
}

/** Extracted from `projectSideViewStraight` to keep its complexity low: run/rise dims, angle arc + Blondel labels. */
function pushStraightDims(primitives: IPlanPrimitive[], ctx: IStraightCtx, measurer: ITextMeasurer): void {
    const { wx, wy, ws, viewTopY, totalUnfoldRun, railingH, h, bAvg, angleRad, result } = ctx;

    // ── Bottom dim — unfolded run ──
    primitives.push(horizontalDim(wx(0), wy(0) + 18, wx(totalUnfoldRun), `${String(Math.round(totalUnfoldRun))} мм`, measurer));

    // ── Right dim — visible rise ──
    primitives.push(verticalDim(wx(totalUnfoldRun) + 22, wy(0), wy(viewTopY), `${String(Math.round(viewTopY))} мм`, measurer));

    // ── Angle indicator ──
    const arcR: number = Math.min(40, ws(bAvg) * 1.6);
    primitives.push({
        kind: EPrimitiveKind.Arc,
        center: [wx(0), wy(0)],
        radius: arcR,
        startAngle: -angleRad,
        endAngle: 0,
        stroke: EColorRef.Err,
        width: 0.8,
    });
    primitives.push(
        text(
            [wx(0) + arcR + 4, wy(0) - arcR / 2 - 2],
            `${result.stairAngle.toFixed(1)}°`,
            ETextAlign.Left,
            ETextBaseline.Middle,
            EColorRef.Err,
            F_500_10
        )
    );

    // ── Blondel + h × b labels (top-left corner) ──
    primitives.push(
        note(
            text(
                [wx(0) + 8, wy(viewTopY + railingH * 0.6)],
                `h ${String(Math.round(h))} · b ${String(Math.round(bAvg))} · 2h+b ${String(result.blondelValue)}`,
                ETextAlign.Left,
                ETextBaseline.Top,
                EColorRef.Ink3,
                F_400_9
            )
        )
    );
}

/**
 * Straight side view (вид сбоку) — the unfolded stair profile built from the
 * sorted step/landing segments: floor lines, the upper-floor slab with the
 * opening cut, the stringer (косоур sawtooth or тетива plank), risers/treads,
 * the straight handrail with balusters, the optional upper-floor railing and
 * the run/rise/angle/Blondel dimensions. Pane-local pixels.
 */
interface IStraightFit {
    scale: number;
    /** How far floor 1 rises above the pane's bottom padding, in pixels. */
    lift: number;
}

/**
 * Fit of the straight profile. The sheet keeps a fixed vertical scale — constant px/mm across
 * types so the schema doesn't jump — and pins floor 1 to the pane bottom. The side-only canvas
 * is one tall pane: there the width bounds the fit as well, and the drawing is centred vertically.
 */
function fitStraightProfile(innerW: number, innerH: number, worldW: number, worldH: number, sideOnly: boolean): IStraightFit {
    const fitHeight: number = Math.max(worldH, SIDE_VIEW_REF_HEIGHT_MM);
    const heightFit: number = innerH / fitHeight;
    if (!sideOnly) {
        return { scale: heightFit, lift: 0 };
    }
    const scale: number = Math.min(heightFit, innerW / worldW);
    return { scale, lift: Math.max(0, (innerH - fitHeight * scale) / 2) };
}

function projectSideViewStraight(
    result: IStairCalculationResult,
    config: IStairConfig,
    pane: IPaneRect,
    measurer: ITextMeasurer,
    sideOnly: boolean,
    fit?: IViewFit
): IProjectionOutput {
    const slab: number = result.room.floorThickness;
    const totalRise: number = result.room.floorHeight;

    const railingH: number = config.railing.enabled ? config.railing.height : 0;
    const treadT: number = config.treadThickness;
    const nose: number = config.noseLength;

    const N: number = result.stepCount;
    const h: number = result.riserHeight;
    const bAvg: number = result.treadDepth;

    if (N <= 0 || totalRise <= 0) {
        return emptyProjection();
    }

    const sortedSteps: IStepGeometry[] = [...result.geometry.steps].sort((a: IStepGeometry, b: IStepGeometry) => a.index - b.index);
    const sortedLandings: ILanding[] = [...result.geometry.landings]
        .map((l: ILandingGeometry): ILanding => ({ y: l.position.y, depth: l.depth, thickness: l.thickness }))
        .sort((a: ILanding, b: ILanding) => a.y - b.y);

    const segments: ISegment[] = buildStraightSegments(sortedSteps, sortedLandings);

    // L/U-shape are handled by the LU projection; this branch is straight-only.
    const truncatedAtTurn: boolean = false;
    const cumX: number = segments.length > 0 ? segments[segments.length - 1].xEnd : 0;
    const totalUnfoldRun: number = cumX > 0 ? cumX : result.totalRun;
    if (segments.length === 0 || totalUnfoldRun <= 0) {
        return emptyProjection();
    }
    // Ветка прямой лестницы обрыва на повороте не знает (truncatedAtTurn здесь всегда
    // false), поэтому верх виден целиком — до полной высоты подъёма.
    const visibleTopY: number = totalRise;

    // World extent — covers the unfolded stair, slab above, railing
    const xMargin: number = Math.max(200, totalUnfoldRun * 0.06);
    const worldXMin: number = -xMargin;
    const worldXMax: number = totalUnfoldRun + xMargin;
    const worldYMin: number = -50;
    const worldYMax: number = visibleTopY + Math.max(slab, railingH) + 50;
    const worldH: number = worldYMax - worldYMin;

    const padL: number = 56;
    const padR: number = 40;
    const padT: number = 38;
    const padB: number = 50;
    const innerW: number = pane.w - padL - padR;
    const innerH: number = pane.h - padT - padB;
    const worldW: number = worldXMax - worldXMin;

    // Center horizontally within the inner pane area; pin world y=0 (floor 1) to the BOTTOM of the pane.
    // A sheet fit places the origin itself.
    const { scale, ox, oy }: IElevationFrame = elevationFrame(fit, (): IElevationFrame => {
        const own: IStraightFit = fitStraightProfile(innerW, innerH, worldW, worldH, sideOnly);
        return {
            scale: own.scale,
            ox: padL + Math.max(0, (innerW - worldW * own.scale) / 2) - worldXMin * own.scale,
            oy: pane.h - padB - own.lift,
        };
    });

    const wx: IMapFn = (x: number): number => ox + x * scale;
    const wy: IMapFn = (y: number): number => oy - y * scale;
    const ws: IMapFn = (s: number): number => s * scale;

    const viewTopY: number = totalRise;
    const angleRad: number = Math.atan2(viewTopY, totalUnfoldRun);
    const cosA: number = Math.cos(angleRad);
    const sinA: number = Math.sin(angleRad);
    const isMonoStringer: boolean = config.stringerType === 'kosour';

    const ctx: IStraightCtx = {
        result,
        config,
        segments,
        wx,
        wy,
        ws,
        viewTopY,
        totalRise,
        totalUnfoldRun,
        worldXMin,
        slab,
        railingH,
        treadT,
        nose,
        h,
        bAvg,
        cosA,
        sinA,
        angleRad,
        isMonoStringer,
        truncatedAtTurn,
    };

    const primitives: IPlanPrimitive[] = [];

    // ── Floor 1 (bottom) ──
    primitives.push(line([wx(worldXMin), wy(0)], [wx(worldXMax), wy(0)], EColorRef.Ink, 1));

    // ── Floor 2 + opening — only meaningful for the straight type (single flight reaches the upper floor).
    pushStraightFloor2(primitives, ctx);

    // 1st floor label
    primitives.push(
        level(
            text(
                [wx(worldXMin) + 6, wy(0) - 3],
                $localize`:@@planLevelGround:1-Й ЭТАЖ`,
                ETextAlign.Left,
                ETextBaseline.Bottom,
                EColorRef.Ink3,
                F_400_9
            )
        )
    );

    // ── Stringer — geometry depends on stringerType ──
    pushStraightStringer(primitives, ctx);

    // ── Risers + treads ──
    pushStraightSteps(primitives, ctx);

    // ── Railing — STRAIGHT handrail along stair angle, balusters end exactly at handrail ──
    pushStraightRailing(primitives, ctx);

    // ── Dimensions, angle indicator + Blondel labels ──
    pushStraightDims(primitives, ctx, measurer);
    pushElevationTreadMarks(primitives, result, straightElevation(ctx), measurer);
    pushElevationRunLengths(primitives, result, config, straightElevation(ctx));

    // ── Pitch line — the dashed Blondel line of the «Фасады» canvas, floor start to the top ──
    if (sideOnly) {
        primitives.push(line([wx(0), wy(0)], [wx(totalUnfoldRun), wy(viewTopY)], EColorRef.Warn, 1.2, { dash: [6, 4] }));
    }

    // Millimetre origin: the first floor at the start of the unfolded run (world x = 0, y = 0).
    return { primitives, pxPerMm: scale, origin: { x: ox, y: oy } };
}

/**
 * The screen-horizontal coordinate of a plan point in a spiral elevation, measured from the
 * pole: world Z for the side view (looking along X), world X for the front view (looking along Z).
 */
type ISpiralAcross = (point: { x: number; z: number }) => number;

/** Extracted from `projectSpiralSideView` to keep its complexity low: projects one wedge step onto (Z, Y) as a two-tone bar. */
function pushSpiralStepBar(
    primitives: IPlanPrimitive[],
    step: IStepGeometry,
    across: ISpiralAcross,
    treadT: number,
    wx: IMapFn,
    wy: IMapFn,
    ws: IMapFn
): void {
    const corners: NonNullable<IStepGeometry['winderCorners']> | undefined = step.winderCorners;
    if (!corners) {
        return;
    }
    const zs: number[] = [corners.innerBack, corners.innerFront, corners.outerBack, corners.outerFront].map(across);
    const innerZs: number[] = [corners.innerBack, corners.innerFront].map(across);
    const zMin: number = Math.min(...zs);
    const zMax: number = Math.max(...zs);
    const innerNearestZ: number = Math.abs(innerZs[0]) < Math.abs(innerZs[1]) ? innerZs[0] : innerZs[1];
    const dirSign: number = innerNearestZ >= 0 ? 1 : -1;
    const inEdgeZ: number = dirSign > 0 ? Math.min(...innerZs) : Math.max(...innerZs);
    const outEdgeZ: number = dirSign > 0 ? zMax : zMin;
    const totalLen: number = Math.abs(outEdgeZ - inEdgeZ);
    if (totalLen < 1) {
        return;
    }

    const stepYTop: number = step.position.y;
    const yTopPx: number = wy(stepYTop);
    const yBotPx: number = wy(stepYTop - treadT);
    const barH: number = yBotPx - yTopPx;

    const brownRatio: number = 0.32;
    const brownLen: number = totalLen * brownRatio;
    const brownStart: number = inEdgeZ;
    const brownEnd: number = inEdgeZ + dirSign * brownLen;
    const yellowStart: number = brownEnd;
    const yellowEnd: number = outEdgeZ;
    const bx1: number = Math.min(brownStart, brownEnd);
    const bx2: number = Math.max(brownStart, brownEnd);
    const yx1: number = Math.min(yellowStart, yellowEnd);
    const yx2: number = Math.max(yellowStart, yellowEnd);

    // Brown segment (inner — closer to pole)
    primitives.push(fillStrokeRect(wx(bx1), yTopPx, ws(bx2 - bx1), barH, EColorRef.Handrail, EColorRef.StepStroke, 1));
    // Yellow segment (outer)
    primitives.push(fillStrokeRect(wx(yx1), yTopPx, ws(yx2 - yx1), barH, EColorRef.Step, EColorRef.StepStroke, 1));
}

/** Extracted from `projectSpiralSideView` to keep its complexity low: projects every wedge step, sorted bottom-to-top. */
function pushSpiralSteps(
    primitives: IPlanPrimitive[],
    result: IStairCalculationResult,
    across: ISpiralAcross,
    treadT: number,
    wx: IMapFn,
    wy: IMapFn,
    ws: IMapFn
): void {
    const sortedSteps: IStepGeometry[] = [...result.geometry.steps].sort(
        (a: IStepGeometry, b: IStepGeometry) => a.position.y - b.position.y
    );
    for (const step of sortedSteps) {
        pushSpiralStepBar(primitives, step, across, treadT, wx, wy, ws);
    }
}

/**
 * Spiral side view — central pole (hatched full-height column) plus each step
 * projected onto the (Z, Y) plane as a two-tone horizontal bar (brown inner
 * third nearest the pole, yellow outer two-thirds), with the total-height,
 * first-riser and pole-width dimensions. Pane-local pixels.
 */
function projectSpiralSideView(
    result: IStairCalculationResult,
    config: IStairConfig,
    pane: IPaneRect,
    measurer: ITextMeasurer,
    opts: { axis?: 'z' | 'x'; fit?: IViewFit }
): IProjectionOutput {
    const totalRise: number = result.room.floorHeight;
    const slab: number = result.room.floorThickness;
    const outerR: number = (config.spiralDiameter ?? 1600) / 2;
    const colR: number = 140; // POLE_RADIUS — see spiral.calculator.ts
    const treadT: number = config.treadThickness;
    const N: number = result.stepCount;

    if (N <= 0 || totalRise <= 0) {
        return emptyProjection();
    }

    // The pole stands at world (outerR, outerR) per spiral.calculator. Looking along Z (the front
    // view) the screen runs along world X; otherwise (the side view) along world Z.
    const across: ISpiralAcross =
        opts.axis === 'z'
            ? (point: { x: number; z: number }): number => point.x - outerR
            : (point: { x: number; z: number }): number => point.z - outerR;
    const worldZSpan: number = outerR * 2;
    const worldH: number = totalRise + slab;

    const padL: number = 56;
    const padR: number = 56;
    const padT: number = 32;
    const padB: number = 56;
    const innerW: number = pane.w - padL - padR;
    const innerH: number = pane.h - padT - padB;
    // The frame's origin is the pole axis at floor level.
    const frame: IElevationFrame | null = elevationFrame(opts.fit, (): IElevationFrame | null => {
        if (innerW <= 0 || innerH <= 0) {
            return null;
        }
        const own: number = Math.min(innerW / worldZSpan, innerH / Math.max(worldH, SIDE_VIEW_REF_HEIGHT_MM));
        return { scale: own, ox: padL + Math.max(0, (innerW - worldZSpan * own) / 2) + outerR * own, oy: pane.h - padB };
    });
    if (!frame) {
        return emptyProjection();
    }
    const { scale, ox, oy }: IElevationFrame = frame;
    const wx: IMapFn = (zRel: number): number => ox + zRel * scale;
    const wy: IMapFn = (y: number): number => oy - y * scale;
    const ws: IMapFn = (s: number): number => s * scale;

    const primitives: IPlanPrimitive[] = [];

    // ── Central pole (full-height hatched rectangle, centered at zRel=0) ──
    primitives.push(fillStrokeRect(wx(-colR), wy(worldH), ws(colR * 2), ws(worldH), EColorRef.Paper2, EColorRef.Ink2, 1));
    primitives.push({
        kind: EPrimitiveKind.Hatch,
        rect: { x: wx(-colR), y: wy(worldH), w: ws(colR * 2), h: ws(worldH) },
        step: 14,
        color: EColorRef.Ink3,
        alpha: 0.32,
        width: 0.6,
    });

    // ── Steps — project each wedge onto the (Z, Y) plane using winderCorners ──
    pushSpiralSteps(primitives, result, across, treadT, wx, wy, ws);

    // ── Railing — from the calculation's railing geometry, as on every elevation ──
    pushElevationRailings(primitives, result, config, { across, wx, wy });

    // ── Dimensions ──
    primitives.push(verticalDim(wx(outerR) + 22, wy(0), wy(worldH), String(Math.round(worldH)), measurer));

    // The pole width, dimensioned under the floor line: at the top the railing runs over the pole.
    primitives.push(horizontalDim(wx(-colR), wy(0) + SPIRAL_POLE_DIM_OFFSET, wx(colR), String(Math.round(colR * 2)), measurer));

    // Millimetre origin: the pole axis at floor level (zRel = 0, y = 0).
    return { primitives, pxPerMm: scale, origin: { x: wx(0), y: oy } };
}

/**
 * Side view (вид сбоку / разрез) — dispatches by stair type, mirroring the
 * former PlanViewComponent#drawSideView: spiral → central-pole projection,
 * l-shape/u-shape → the per-axis stacked projection, straight → the unfolded
 * profile. Pure projection in pane-local pixels; the drawer translates to the
 * pane origin. `opts.axis` selects the viewing axis of the L/U and spiral elevations — 'z' looks
 * along Z (the front view, screen X = world X), 'x' along X; `opts.sideOnly` marks the «Фасады»
 * canvas: the straight profile gets the dashed pitch line and fits the pane width too; `opts.fit`
 * is the sheet's shared scale and origin point.
 */
export function projectSideView(
    result: IStairCalculationResult,
    config: IStairConfig,
    pane: IPaneRect,
    measurer: ITextMeasurer,
    opts?: { axis?: 'z' | 'x'; sideOnly?: boolean; fit?: IViewFit }
): IProjectionOutput {
    if (config.type === 'spiral') {
        return projectSpiralSideView(result, config, pane, measurer, { axis: opts?.axis ?? 'x', fit: opts?.fit });
    }
    if (config.type === 'l-shape' || config.type === 'u-shape') {
        return projectSideViewLU(result, config, pane, measurer, opts?.axis ?? 'z', opts?.fit);
    }
    return projectSideViewStraight(result, config, pane, measurer, opts?.sideOnly ?? false, opts?.fit);
}
